import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import AuthInput from "../AuthInput";

const OtpStep = ({
  step,
  title,
  email,
  otp,
  setOtp,
  setError,
  onVerify,
  onBack,
  onSkip,
  onResend,
  seconds,
  error,
  loading,
  allowSkip = false,
  isVerifyEmailFlow = false,
  infoText,
  expired = false,
  expiryMessage,
}) => {
  const [timer, setTimer] = useState(seconds);
  const [showAdminPopup, setShowAdminPopup] = useState(false);

  // Update local timer when the prop changes
  useEffect(() => {
    setTimer(seconds);
  }, [seconds]);

  // Countdown effect
  useEffect(() => {
    if (timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const formatTime = (totalSeconds) => {
    const safeValue = Number.isFinite(totalSeconds)
      ? Math.max(totalSeconds, 0)
      : 0;
    const mins = Math.floor(safeValue / 60);
    const secs = safeValue % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleResend = () => {
    if (timer > 0) return; // Safety check
    onResend(); // Trigger parent resend logic
    setTimer(seconds); // Reset timer
  };

  return (
    <div>
      {/* <h2 className="text-base font-semibold text-center pt-2">{title}</h2> */}

      {isVerifyEmailFlow && email ? (
        <p className="w-full sm:w-[50%] mx-auto text-sm text-primary text-center pt-2 pb-3">
          We&apos;ve sent a one-time code to{" "}
          <span className="font-semibold text-secondary">{email}</span> to
          verify your email.
        </p>
      ) : null}

      <div className="mt-1">
        <AuthInput
          step={step}
          setValue={setOtp}
          name="otp"
          setError={setError}
          value={otp}
          onChange={(e) => {
            setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
          }}
          placeholder="Enter the OTP"
          buttonText="Verify OTP"
          onButtonClick={onVerify}
          loading={loading}
          error={error}
          disabled={expired}
        />
      </div>

      {expired && expiryMessage && (
        <p className="text-xs text-red-600 mt-2 text-center">{expiryMessage}</p>
      )}

      <div className="w-full sm:w-[50%] mx-auto flex items-center justify-between pt-3">
        {Number.isFinite(timer) && (
          <p className="text-sm text-gray-500">
            OTP expires in{" "}
            <span className="text-red-500 font-semibold">
              {formatTime(timer)}
            </span>
          </p>
        )}
        <button
          type="button"
          onClick={handleResend}
          disabled={timer > 0}
          className={`text-sm ${timer > 0 ? "text-gray-400 cursor-not-allowed" : "text-buttonColor underline"}`}
        >
          Resend OTP
        </button>
      </div>

      {!allowSkip && !isVerifyEmailFlow ? (
        <p className="text-base text-primary mt-2 text-center pt-2 pb-4">
          If <span className="text-secondary">OTP</span> is not received, please
          check your <span className="text-secondary">spam</span> folder or{" "}
          <span className="text-secondary">try again</span> later.
        </p>
      ) : null}
      {infoText && (
        <div className={`${isVerifyEmailFlow ? "mt-4 space-y-2" : "mt-5"}`}>
          <p className="text-primary">
            Please check your <span className="text-secondary">Inbox</span> for
            the verification code. If you don't see it, be sure to look in your
            <span className="text-secondary"> Spam or Junk folder</span>.
          </p>
          <p className="text-primary">
            {isVerifyEmailFlow ? (
              <>
                If you don’t receive the code before it expires, you can{" "}
                <button
                  type="button"
                  onClick={() => setShowAdminPopup(true)}
                  className="text-buttonColor underline"
                >
                  contact administrator
                </button>{" "}
                or come back later to verify your email.
              </>
            ) : (
              <>
                Your code is valid for{" "}
                <span className="text-secondary">60 minutes</span>. If it
                expires, select{" "}
                {allowSkip && onSkip ? (
                  <button
                    type="button"
                    onClick={onSkip}
                    className="text-buttonColor underline"
                  >
                    Skip &amp; Create Password
                  </button>
                ) : (
                  "skip this step for now and create your password"
                )}{" "}
                to continue.
              </>
            )}
          </p>
          {!isVerifyEmailFlow ? (
            <>
              {/* <p className="text-primary">
                Email verification is important because all trip updates and
                messages will be sent to this verified email.
              </p> */}

              <p className="text-primary p-1">
                You can verify your email later anytime from the login screen.
              </p>
            </>
          ) : null}
        </div>
      )}

      {showAdminPopup ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full text-center">
            <Info
              size={34}
              className="mx-auto text-blue-600"
              aria-hidden="true"
            />
            <h3 className="text-base font-semibold pt-2">
              Contact Administrator
            </h3>
            <p className="text-sm text-primary pt-2">
              Need help with login? Contact us:
            </p>
            <p className="text-sm text-primary pt-2">
              Call:{" "}
              <a
                href="tel:+18604022247"
                className="text-secondary underline font-semibold"
              >
                +1 860 402 2247
              </a>
            </p>
            <p className="text-sm text-primary pt-1">
              Email:{" "}
              <a
                href="mailto:info@biblicaltouring.com"
                className="text-secondary underline font-semibold"
              >
                info@biblicaltouring.com
              </a>
            </p>
            <button
              type="button"
              onClick={() => setShowAdminPopup(false)}
              className="mt-4 bg-buttonColor text-white text-xs px-4 py-2 rounded-md"
            >
              Ok
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OtpStep;
